import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { generateInstallments } from "../components/Financial/InstallmentList/installmentHelper";

export const BILLING_RECEIVABLE_ID = 'billingReceivableId'
export const BILLING_PAYABLE_ID = 'billingPayableId'

/**
 * Custom React hook to manage billing installments logic for either receivables or payables.
 *
 * @param {Object} params - The parameters for the hook.
 * @param {Object} params.values - The current form state or values related to the billing data.
 * @param {Function} params.setValues - A state setter function to update the `values` object.
 * @param {'billingPayableId'|'billingReceivableId'} params.installmentIdKey - 
 *   Determines which field to use as the foreign key in installment items.
 *
 * @returns {Object} An object containing helper functions and computed values related to billing installments.
 */
export default function useBillingInstallments({
    values,
    setValues,
    installmentIdKey,
  }) {

  const [installmentsCount, setInstallmentsCount] = useState(values?.billingInstallments?.length || 1);
  const lastInstallmentsCount = useRef(values?.billingInstallments?.length || 1);
  const originalAmount = useRef(values?.amount);
  const originalDueDate = useRef(values?.dueDate);
  const [warnUser, setWarnUser] = useState(true);
  const [createInstallmentsList, setCreateInstallmentsList] = useState(false);

  // Logic to decide if we need to (re)create installments
  const hasToCreateInstallmentsList = () => {
    // create installments list ONLY if one this conditions happen:

    // - there is NO installments list (from database or deleted from user interface) OR
    if (values?.billingInstallments?.length === 0) return true;

    // - there is any changes in installments count OR
    if (Number(installmentsCount) !== Number(lastInstallmentsCount.current)) return true;

    // - there IS an installments list already created but the amount or dueDate has changed
    if (
      Number(originalAmount.current) !== Number(values.amount) ||
      Number(originalDueDate.current) !== Number(values.dueDate)
    ) return true;

    return false;
  };

  useEffect(() => {
    setCreateInstallmentsList(hasToCreateInstallmentsList());
    // eslint-disable-next-line
  }, [
    values.billingInstallments,
    installmentsCount,
    lastInstallmentsCount,
    originalAmount,
    values.amount,
    originalDueDate,
    values.dueDate,
  ]);

  useEffect(() => {
    setInstallmentsCount(values?.billingInstallments?.length || 1);
    // eslint-disable-next-line
  }, [values?.billingInstallments]);

  useEffect(() => {
    if (!installmentsCount) setInstallmentsCount(1);

    if (Number(installmentsCount) === 1) {
      setValues(prev => ({
        ...prev,
        billingInstallments: []
      }));
      return;
    }

    if (createInstallmentsList) {
      if (warnUser && !!values.id) {
        toast.warn('Parcelas serão recriadas e os dados anteriores perdidos. Para cancelar, feche e abra novamente a edição da conta.');
        setWarnUser(false);
      }

      const generatedInstallments = generateInstallments({
        installmentNumbers: installmentsCount,
        amount: values.amount,
        dueDate: values.dueDate,
        [installmentIdKey]: values.id,
      });

      setValues(prev => ({
        ...prev,
        billingInstallments: generatedInstallments
      }));

      lastInstallmentsCount.current = Number(installmentsCount);
    }
    // eslint-disable-next-line
  }, [installmentsCount, lastInstallmentsCount, values.amount, values.dueDate, values.id]);

  const handleChangeInstallmentCounts = (e) => {
    const { value } = e.target;
    setInstallmentsCount(Number(value));
  };

  return {
    installmentsCount,
    handleChangeInstallmentCounts,
  };
}